import type { PushConfig } from '@cf/shared';

import { api } from './api';

/**
 * Phone notifications (Web Push). States the UI shows:
 * - insecure: page not served over HTTPS (or localhost) — push is impossible
 * - ios-install: iPhone / iPad Safari — push only works in the app installed on the home screen
 * - unsupported, denied (blocked in the browser settings), off, on
 */
export type PushState = 'insecure' | 'ios-install' | 'unsupported' | 'denied' | 'off' | 'on';

const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (!window.isSecureContext) return 'insecure';
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return isIos() ? 'ios-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return (await currentSubscription()) ? 'on' : 'off';
}

/** Makes sure the server knows this device (idempotent: safe to call on every visit). */
export async function syncSubscription(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) await api('/notifications/subscriptions', { method: 'POST', json: sub.toJSON() });
}

function base64UrlToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const padded = (b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bin = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function enablePush(config: PushConfig): Promise<PushState> {
  if (!config.publicKey) return 'unsupported';
  if ((await Notification.requestPermission()) !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(config.publicKey),
    }));
  await api('/notifications/subscriptions', { method: 'POST', json: sub.toJSON() });
  return 'on';
}

/** Stops alerts on this device (server first, then the browser). Best effort. */
export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const sub = await currentSubscription();
  if (!sub) return;
  await api('/notifications/subscriptions/remove', {
    method: 'POST',
    json: { endpoint: sub.endpoint },
  }).catch(() => undefined);
  await sub.unsubscribe();
}
